import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { Id } from '@/infra/id'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
} from '@/utils/validx-util'
import { PersonalInfoEditorViewModel } from './PersonalInfoEditorViewModel'
import type { DependentProfileVersion } from './DependentProfileVersion'

interface DependentBaseline {
  firstName: string
  middleInitial: string
  lastName: string
  dateOfBirth: string
  personalTaxpayerIdentificationNumber: string
  relationship: string
}

/**
 * Editor for a dependent profile.
 */
export class DependentProfileEditorViewModel {
  @observable accessor id = Id.none
  @observable accessor version = 0
  @observable private accessor _relationship = ''
  @observable.ref private accessor _baseline: DependentBaseline | null = null

  readonly personalInfoVm = new PersonalInfoEditorViewModel()

  readonly validation = validationContext(this, {
    personalInfoVm: [validateObject()],
    relationship: [
      required('Relationship is required'),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Relationship must not exceed 100 characters'),
    ],
  })

  @computed get relationship() {
    return this._relationship
  }

  set relationship(value: string) {
    this._relationship = value
    this.validation.clearErrors('relationship')
  }

  /**
   * Whether the editor differs from the profile it was reset to. A new
   * dependent is always dirty.
   */
  @computed get isDirty() {
    const baseline = this._baseline
    if (!baseline) {
      return true
    }

    return (
      this.personalInfoVm.firstName !== baseline.firstName ||
      this.personalInfoVm.middleInitial !== baseline.middleInitial ||
      this.personalInfoVm.lastName !== baseline.lastName ||
      this.personalInfoVm.dateOfBirth !== baseline.dateOfBirth ||
      this.personalInfoVm.personalTaxpayerIdentificationNumber !==
        baseline.personalTaxpayerIdentificationNumber ||
      this.relationship !== baseline.relationship
    )
  }

  /**
   * Resets the editor to the given profile, or to defaults.
   */
  @action.bound
  reset(profile?: DependentProfileVersion | null) {
    this.id = profile?.id ?? Id.none
    this.version = profile?.version ?? 0
    this.personalInfoVm.reset(profile?.personalInfo)
    this.relationship = profile?.relationship ?? ''
    this._baseline = profile
      ? {
          firstName: this.personalInfoVm.firstName,
          middleInitial: this.personalInfoVm.middleInitial,
          lastName: this.personalInfoVm.lastName,
          dateOfBirth: this.personalInfoVm.dateOfBirth,
          personalTaxpayerIdentificationNumber:
            this.personalInfoVm.personalTaxpayerIdentificationNumber,
          relationship: this.relationship,
        }
      : null
    this.validation.reset()
  }
}
